import { Button, ButtonGroup, ButtonGroupItem, ChipConfirmModal, Input } from "@ondit/ui";
import { ExternalLink, LogOut, Monitor, Moon, Rows3, Rows4, Sun, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { useAccount } from "~/common/account/account";
import { ApiError, api, logoutUrl, type Me, type Org } from "~/core/api/index";
import { LOCALES, type Locale, pick, useI18n, useT } from "~/core/i18n";
import { type Density, useDensity } from "~/core/preferences/density";
import { type Theme, useTheme } from "~/core/preferences/theme";

const SITE = import.meta.env.VITE_SITE_ORIGIN || "http://localhost:9901";

/** Who is signed in: the section about the person rather than the work. */
export function ProfilePanel({ me, org }: { me: Me; org: Org }) {
	const t = useT();
	const { locale } = useI18n();
	const open = useAccount();
	const [sending, setSending] = useState(false);
	const [closing, setClosing] = useState(false);
	const [closingOpen, setClosingOpen] = useState(false);
	const [name, setName] = useState(me.user.name ?? "");
	const [saving, setSaving] = useState(false);
	const who = me.user.name || me.user.email;
	const trimmed = name.trim();
	const dirty = trimmed !== (me.user.name ?? "").trim();
	const joinedAt = org.joined_at ? new Date(org.joined_at) : null;
	const joined =
		joinedAt && !Number.isNaN(joinedAt.getTime())
			? joinedAt.toLocaleDateString(pick(locale, ["en-GB", "zh-CN"]), {
					year: "numeric",
					month: "short",
					day: "numeric",
				})
			: null;

	/*
	 * The name is the one field on this screen the panel writes. It is saved on
	 * demand rather than on blur: a name is short enough that an accidental
	 * autosave of a half-typed one is a worse outcome than a click, and a person
	 * who opened settings to read their plan should be able to leave the field
	 * alone without having written to their own account.
	 */
	async function saveName() {
		setSaving(true);
		try {
			await api.updateProfile(trimmed);
			me.user.name = trimmed;
			toast.success(t("account.nameSaved"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setSaving(false);
		}
	}

	/*
	 * A password is changed the way a forgotten one is recovered: by a link to
	 * the address on the account. The panel never holds the old password, so
	 * asking for it here would buy nothing the mailbox does not already prove.
	 */
	async function sendReset() {
		setSending(true);
		try {
			await api.forgotPassword(me.user.email);
			toast.success(t("account.passwordSent"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setSending(false);
		}
	}

	/*
	 * Closing the account. The server decides what that means — the accounts
	 * this person owns go with their brands, the ones they were invited to
	 * only lose them — and it refuses an owned account that still has
	 * colleagues in it, which is the sentence the toast shows.
	 *
	 * The session is already dead by the time this returns, so the panel goes
	 * to the sign-out URL rather than re-rendering screens whose next request
	 * would 401.
	 */
	async function closeAccount() {
		setClosing(true);
		try {
			await api.closeAccount();
			toast.success(t("account.closed"));
			window.location.href = logoutUrl();
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setClosing(false);
		}
	}

	return (
		<>
			<div className="account-identity">
				<span className="account-mark" aria-hidden>
					{who.slice(0, 1)}
				</span>
				<div className="account-identity-text">
					<strong>{who}</strong>
					<span className="muted">{me.user.email}</span>
				</div>
			</div>

			{/* Which account, as what, since when. Three facts a person checks
			    before they act on anything else in settings — a colleague who
			    cannot find the seat controls is usually a member, not an admin,
			    and the answer is on this line. */}
			<dl className="account-facts">
				<div>
					<dt>{t("account.org")}</dt>
					<dd>{org.name}</dd>
				</div>
				<div>
					<dt>{t("account.role")}</dt>
					<dd>{t(`members.role.${org.role}`)}</dd>
				</div>
				<div>
					<dt>{t("account.joined")}</dt>
					{/* An em dash rather than "Invalid Date": a panel talking to an
					    older API has no joined date, and the reader should see a
					    missing fact rather than a broken one. */}
					<dd>{joined ?? "—"}</dd>
				</div>
			</dl>

			<Row label={t("account.name")} hint={t("account.nameHint")}>
				<Input
					aria-label={t("account.name")}
					value={name}
					maxLength={80}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && dirty && trimmed) void saveName();
					}}
				/>
				<Button variant="default" onClick={() => void saveName()} disabled={saving || !dirty || !trimmed}>
					{t("account.save")}
				</Button>
			</Row>

			<Row label={t("account.password")} hint={t("account.passwordHint", { email: me.user.email })}>
				<Button variant="default" onClick={sendReset} disabled={sending}>
					{t("account.passwordSend")}
				</Button>
			</Row>

			<Row label={t("billing.plan")} hint={org.plan_key}>
				<Button variant="default" onClick={() => open("subscription")}>
					{t("account.subscription")}
				</Button>
			</Row>

			<Row label={t("account.members")} hint={t("account.membersHint")}>
				<Button variant="default" onClick={() => open("members")}>
					{t("members.open")}
				</Button>
			</Row>

			<Row label={t("account.directory")} hint={t("account.directoryHint")}>
				<Button asChild variant="default">
					<a href={SITE}>
						<ExternalLink />
						{t("account.openLink")}
					</a>
				</Button>
			</Row>

			<Row label={t("nav.signOut")} hint={t("account.signOutHint")}>
				<Button asChild variant="default">
					<a href={logoutUrl()}>
						<LogOut />
						{t("nav.signOut")}
					</a>
				</Button>
			</Row>

			{/* Last on the screen, and the only destructive control on it. */}
			<Row label={t("account.close")} hint={t("account.closeHint")}>
				<Button
					variant="ghost"
					className="text-danger hover:text-danger hover:bg-danger/10"
					disabled={closing}
					onClick={() => setClosingOpen(true)}
				>
					<Trash2 />
					{t("account.close")}
				</Button>
			</Row>
			<ChipConfirmModal
				open={closingOpen}
				onOpenChange={setClosingOpen}
				defaultAction="none"
				title={t("account.closeTitle")}
				text={t("account.closeBody")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("account.close"),
					variant: "destructive",
					onClick: () => {
						setClosingOpen(false);
						void closeAccount();
					},
				}}
			/>
		</>
	);
}

/**
 * Theme, language and density: the three answers that change how the panel
 * reads rather than what it holds. Each is one row, so the page is short
 * enough that sending somebody to "Preferences" is not a search.
 *
 * Density is not an appearance setting in disguise: it changes how much of a
 * table fits on a screen, which for an account with three hundred questions is
 * the difference between reading and scrolling.
 */
export function PreferencesPanel() {
	const t = useT();
	const { theme, setTheme } = useTheme();
	const { locale, setLocale } = useI18n();
	const { density, setDensity } = useDensity();

	return (
		<>
			<Row label={t("theme.label")} hint={t("account.themeHint")}>
				<Seg
					label={t("theme.label")}
					value={theme}
					onChange={(next) => setTheme(next)}
					options={[
						{ value: "light" as Theme, label: t("theme.light"), icon: <Sun /> },
						{ value: "dark" as Theme, label: t("theme.dark"), icon: <Moon /> },
						{ value: "system" as Theme, label: t("theme.system"), icon: <Monitor /> },
					]}
				/>
			</Row>
			<Row label={t("nav.language")} hint={t("account.languageHint")}>
				<Seg
					label={t("nav.language")}
					value={locale}
					onChange={(next) => setLocale(next)}
					options={LOCALES.map((code) => ({ value: code as Locale, label: pick(code, ["English", "中文"]) }))}
				/>
			</Row>
			<Row label={t("density.label")} hint={t("account.densityHint")}>
				<Seg
					label={t("density.label")}
					value={density}
					onChange={(next) => setDensity(next)}
					options={[
						{ value: "comfortable" as Density, label: t("density.comfortable"), icon: <Rows3 /> },
						{ value: "compact" as Density, label: t("density.compact"), icon: <Rows4 /> },
					]}
				/>
			</Row>
		</>
	);
}

/** What this setting is, what it does, and the control that changes it. */
export function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
	return (
		<div className="account-row">
			<div className="account-row-text">
				<span className="account-row-label">{label}</span>
				{hint ? <span className="hint">{hint}</span> : null}
			</div>
			<div className="account-row-control">{children}</div>
		</div>
	);
}

/**
 * A handful of values, all of them visible.
 *
 * A select would hide two of three behind a click for no saving in width, and
 * these are settings somebody flips back and forth — a reader checking how a
 * page looks dark, a bilingual customer checking a translation.
 */
export function Seg<T extends string>({
	label,
	value,
	options,
	onChange,
}: {
	label: string;
	value: T;
	options: { value: T; label: string; icon?: ReactNode }[];
	onChange: (next: T) => void;
}) {
	return (
		<ButtonGroup
			value={value}
			onValueChange={(next) => {
				if (next) onChange(next as T);
			}}
			aria-label={label}
		>
			{options.map((option) => (
				<ButtonGroupItem key={option.value} value={option.value}>
					{option.icon}
					{option.label}
				</ButtonGroupItem>
			))}
		</ButtonGroup>
	);
}
